import React, { useEffect, useState, useMemo, useContext } from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import { AuthContext } from '../../context/AuthContext';
import { SimpleTreeView, TreeItem } from '@mui/x-tree-view';
import {
  FolderOpen as FolderOpenIcon,
  Close as CloseIcon,
  Delete as DeleteIcon,
  Search as SearchIcon
} from '@mui/icons-material';
import { IconButton, Radio } from '@mui/material';
import './OrgTreeSelect.css';

const UserRow = React.memo(({ user, isChecked, onAction }) => {
  const handlePointerDown = (e) => {
    e.preventDefault();
    onAction(user);
  };

  return (
    <tr
      className={isChecked ? 'selected' : ''}
      style={{ cursor: 'pointer', userSelect: 'none' }}
    >
      <td className="text-center" onPointerDown={handlePointerDown}>
        <Radio checked={isChecked} size="small" sx={{ padding: '2px', pointerEvents: 'none' }} />
      </td>
      <td onPointerDown={handlePointerDown}>{user.name}</td>
      <td onPointerDown={handlePointerDown}>{user.position}</td>
      <td onPointerDown={handlePointerDown}>{user.userid}</td>
    </tr>
  );
});

export default function SafetyApprovalLineModal({
  writer,
  onConfirm,
  onCancel,
  initialSelected = { drafter: null, reviewer: null, approver: null }
}) {
  const [deptTree, setDeptTree] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [expandedItems, setExpandedItems] = useState([]);

  const [deptSearchInput, setDeptSearchInput] = useState('');
  const [deptSearchTerm, setDeptSearchTerm] = useState('');

  const [userSearchInput, setUserSearchInput] = useState('');
  const [activeUserSearch, setActiveUserSearch] = useState('');

  // drafter, reviewer, approver
  const [selectedRoles, setSelectedRoles] = useState({
    drafter: initialSelected.drafter || writer || null,
    reviewer: initialSelected.reviewer || null,
    approver: initialSelected.approver || null
  });

  const [currentLineType, setCurrentLineType] = useState('reviewer'); // 기본 선택을 검토자로
  const [activeDept, setActiveDept] = useState(null);

  const { token, user: authUser } = useContext(AuthContext);

  useEffect(() => {
    const comCode = authUser?.M_ComCode || sessionStorage.getItem('M_ComCode');
    axios.get('/api/orgchart', {
      headers: {
        'x-com-code': comCode,
        'Authorization': `Bearer ${token}`
      }
    })
      .then(res => {
        const { tree, allDeptCodes: codes, flatUsers, map } = buildDeptTree(res.data);
        setDeptTree(tree);
        setAllUsers(flatUsers);
        setExpandedItems(codes);

        // 1. 현재 로그인한 사용자(또는 기안자)의 부서 코드를 찾는다.
        const targetDeptCode = authUser?.dept_code || writer?.dept_code;
        
        // 2. 해당 부서가 조직도에 있으면 그 부서를 기본으로 열어주고, 없으면 맨 꼭대기를 연다.
        if (targetDeptCode && map[targetDeptCode]) {
          setActiveDept(map[targetDeptCode]);
        } else if (tree.length > 0) {
          setActiveDept(tree[0]);
        }

        // 기안자/검토자/승인자 초기 정보가 넘어오면, 완벽한 조직도 데이터(flatUsers)에서 찾아서 덮어씌움
        setSelectedRoles(prev => {
          const updated = { ...prev };

          // 1. 기안자(drafter) 처리: 넘겨받은 초기 기안자가 있으면 우선 적용, 없으면 현재 로그인 유저(writer) 적용
          if (initialSelected?.drafter?.name || initialSelected?.drafter?.userid) {
            const foundDrafter = flatUsers.find(u =>
              (initialSelected.drafter.userid && String(u.userid) === String(initialSelected.drafter.userid)) ||
              (initialSelected.drafter.name && u.name === initialSelected.drafter.name)
            );
            if (foundDrafter) updated.drafter = foundDrafter;
          } else if (writer && writer.userid) {
            const foundWriter = flatUsers.find(u => String(u.userid) === String(writer.userid));
            if (foundWriter) updated.drafter = foundWriter;
          }

          // 2. 검토자(reviewer) 처리
          if (initialSelected?.reviewer?.name || initialSelected?.reviewer?.userid) {
            const foundReviewer = flatUsers.find(u =>
              (initialSelected.reviewer.userid && String(u.userid) === String(initialSelected.reviewer.userid)) ||
              (initialSelected.reviewer.name && u.name === initialSelected.reviewer.name)
            );
            if (foundReviewer) updated.reviewer = foundReviewer;
          }
          if (initialSelected?.approver?.name || initialSelected?.approver?.userid) {
            const foundApprover = flatUsers.find(u =>
              (initialSelected.approver.userid && String(u.userid) === String(initialSelected.approver.userid)) ||
              (initialSelected.approver.name && u.name === initialSelected.approver.name)
            );
            if (foundApprover) updated.approver = foundApprover;
          }
          return updated;
        });
      })
      .catch(err => console.error('조직도 불러오기 실패:', err));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, authUser, writer]);

  const buildDeptTree = (depts) => {
    const map = {};
    const tree = [];
    const allDeptCodes = [];
    const flatUsers = [];

    depts.forEach(dept => {
      allDeptCodes.push(String(dept.dept_code));
      const usersWithDept = (dept.users || []).map(u => ({
        ...u,
        dept_code: dept.dept_code,
        dept_name: dept.dept_name,
        full_dept_name: dept.full_dept_name
      }));
      flatUsers.push(...usersWithDept);
      map[dept.dept_code] = { ...dept, children: [], users: usersWithDept };
    });

    depts.forEach(dept => {
      if (dept.parent_dept_code && map[dept.parent_dept_code]) {
        map[dept.parent_dept_code].children.push(map[dept.dept_code]);
      } else {
        tree.push(map[dept.dept_code]);
      }
    });

    return { tree, allDeptCodes, map, flatUsers };
  };

  const handleCheck = (userObj) => {
    // Check if user is already assigned to a DIFFERENT role
    const alreadyAssignedRole = Object.keys(selectedRoles).find(
      role => role !== currentLineType && selectedRoles[role]?.userid === userObj.userid
    );

    if (alreadyAssignedRole) {
      alert(`해당 사용자는 이미 '${getRoleName(alreadyAssignedRole)}' 역할로 지정되어 있습니다.`);
      return;
    }

    setSelectedRoles(prev => ({
      ...prev,
      [currentLineType]: prev[currentLineType]?.userid === userObj.userid ? null : userObj
    }));
  };

  const getRoleName = (roleKey) => {
    if (roleKey === 'drafter') return '기안 (관리감독자)';
    if (roleKey === 'reviewer') return '검토 (안전관리자)';
    if (roleKey === 'approver') return '승인 (책임자)';
    return '';
  };

  const runDeptSearch = () => setDeptSearchTerm(deptSearchInput);
  const runUserSearch = () => setActiveUserSearch(userSearchInput);
  const clearUserSearch = () => { setUserSearchInput(''); setActiveUserSearch(''); };

  const displayUsers = useMemo(() => {
    if (!activeUserSearch) return activeDept?.users || [];
    const lowerSearch = activeUserSearch.toLowerCase();
    return allUsers.filter(u =>
      u.name.toLowerCase().includes(lowerSearch) ||
      String(u.userid).toLowerCase().includes(lowerSearch) ||
      (u.position && u.position.toLowerCase().includes(lowerSearch))
    );
  }, [activeUserSearch, activeDept, allUsers]);

  const renderTreeNodes = (nodes) => {
    return nodes.map(node => {
      if (deptSearchTerm && !node.dept_name.includes(deptSearchTerm)) {
        const hasMatchingChild = JSON.stringify(node.children).includes(deptSearchTerm);
        if (!hasMatchingChild) return null;
      }

      const labelContent = (
        <div
          style={{ display: 'flex', alignItems: 'center', padding: '4px 0', width: '100%' }}
          onClick={(e) => {
            e.stopPropagation();
            setActiveDept(node);
            clearUserSearch();
          }}
        >
          <FolderOpenIcon style={{ fontSize: '16px', color: '#ffc107', marginRight: '6px' }} />
          <span style={{ fontSize: '13px' }}>{node.dept_name}</span>
        </div>
      );

      return (
        <TreeItem key={String(node.dept_code)} itemId={String(node.dept_code)} label={labelContent}>
          {node.children && node.children.length > 0 ? renderTreeNodes(node.children) : null}
        </TreeItem>
      );
    });
  };

  const handleConfirm = () => {
    if (!selectedRoles.drafter) {
      alert("기안 (관리감독자)는 필수입니다.");
      return;
    }
    if (!selectedRoles.approver) {
      alert("최종 결재를 위한 승인 (책임자)는 필수입니다.");
      return;
    }
    onConfirm(selectedRoles);
  };

  const renderSelectedCard = (roleKey) => {
    const user = selectedRoles[roleKey];
    const isDrafter = roleKey === 'drafter';

    // 기안자는 연한 회색/블루 배경에 다른 아이콘(열쇠) 사용
    const bgColor = isDrafter ? '#f1f5f9' : '#fff';
    const borderColor = isDrafter ? '#cbd5e1' : '#cbd5e1';

    // 뱃지 타이틀 및 테마 색상 설정
    let badgeText = '';
    let badgeBg = '';
    let badgeColor = '';
    if (roleKey === 'drafter') { badgeText = '기안'; badgeBg = '#fef3c7'; badgeColor = '#d97706'; }
    else if (roleKey === 'reviewer') { badgeText = '검토'; badgeBg = '#e0e7ff'; badgeColor = '#4338ca'; }
    else if (roleKey === 'approver') { badgeText = '승인'; badgeBg = '#ffe4e6'; badgeColor = '#e11d48'; }

    return (
      <div className="mb-2" style={{ border: `1px solid ${borderColor}`, borderRadius: '4px', padding: '8px 12px', display: 'flex', alignItems: 'center', backgroundColor: bgColor }}>
        <div style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
          {isDrafter ? (
            <i className="fas fa-key" style={{ color: '#f59e0b', fontSize: '0.9rem', marginLeft: '0px', marginRight: '6px' }}></i>
          ) : (
            <i className="fas fa-user" style={{ color: '#5c7cfa', fontSize: '0.8rem', marginLeft: '0px', marginRight: '6px' }}></i>
          )}

          {/* 역할 타이틀 뱃지 */}
          <div style={{
            padding: '3px 8px', backgroundColor: badgeBg, color: badgeColor,
            fontSize: '0.75rem', fontWeight: 'bold', borderRadius: '4px',
            marginRight: '10px', minWidth: '40px', textAlign: 'center'
          }}>
            {badgeText}
          </div>

          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {user ? (
              <>
                {/* 윗줄: 이름 + (직위) / 굵고 진하게 */}
                <div style={{ fontSize: '0.8rem', fontWeight: 'bold', color: '#334155', marginBottom: '2px' }}>
                  {user.name} {user.position || user.rank ? `(${user.position || user.rank})` : ''}
                </div>
                {/* 아랫줄: 부서 / 작고 흐리게 */}
                <div style={{ fontSize: '0.7rem', color: '#94a3b8' }}>
                  {user.dept_name || user.department || '부서없음'}
                </div>
              </>
            ) : (
              <>
                <div style={{ fontSize: '0.8rem', fontWeight: 'bold', color: '#94a3b8', marginBottom: '2px' }}>
                  미지정
                </div>
                <div style={{ fontSize: '0.7rem', color: '#94a3b8' }}>
                  {getRoleName(roleKey)}
                </div>
              </>
            )}
          </div>
        </div>
        {user && !isDrafter && (
          <IconButton size="small" style={{ color: '#ef4444', marginRight: '-6px' }} onClick={() => setSelectedRoles(prev => ({ ...prev, [roleKey]: null }))}>
            <DeleteIcon />
          </IconButton>
        )}
      </div>
    );
  };

  return ReactDOM.createPortal(
    <div className="org-tree-popup" onPointerDown={e => e.stopPropagation()}>
      <div className="org-tree-popup-content" style={{ width: '1000px', height: '650px' }}>
        <div className="org-tree-header">
          <h5 className="m-0" style={{ fontSize: '16px', fontWeight: 'bold' }}>🗂️ 결재선 지정 (위험성평가)</h5>
          <IconButton size="small" onClick={onCancel} style={{ color: '#fff' }}><CloseIcon /></IconButton>
        </div>

        <div className="org-tree-body">
          {/* 1. 좌측 패널 (조직 트리) */}
          <div className="org-tree-pane">
            <div className="org-tree-pane-header">
              <span>조직 트리</span>
            </div>
            <div className="p-2 border-bottom d-flex gap-1">
              <div style={{ position: 'relative', flex: 1 }}>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  placeholder="부서명 검색..."
                  value={deptSearchInput}
                  onChange={(e) => setDeptSearchInput(e.target.value)}
                  onKeyPress={(e) => { if (e.key === 'Enter') runDeptSearch(); }}
                />
              </div>
              <button className="btn btn-outline-secondary btn-xs" onClick={runDeptSearch}><SearchIcon style={{ fontSize: '16px' }} /></button>
            </div>
            <div className="org-tree-pane-content premium-tree-root">
              <SimpleTreeView 
                expandedItems={expandedItems} 
                onExpandedItemsChange={(e, items) => setExpandedItems(items)}
                selectedItems={activeDept ? String(activeDept.dept_code) : ''}
              >
                {renderTreeNodes(deptTree)}
              </SimpleTreeView>
            </div>
          </div>

          {/* 2. 중앙 패널 (사용자 검색 및 목록) */}
          <div className="org-tree-pane">
            <div className="org-tree-pane-header">
              <span>👤 {activeUserSearch ? '전사 사용자 검색 결과' : (activeDept ? activeDept.dept_name : '부서원')}</span>
            </div>
            <div className="p-2 border-bottom d-flex gap-1">
              <div style={{ position: 'relative', flex: 1 }}>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  placeholder="찾으실 사원 성명 입력..."
                  value={userSearchInput}
                  onChange={(e) => setUserSearchInput(e.target.value)}
                  onFocus={() => setUserSearchInput('')}
                  onClick={() => setUserSearchInput('')}
                  onKeyPress={(e) => { if (e.key === 'Enter') runUserSearch(); }}
                />
              </div>
              <button className="btn btn-outline-secondary btn-xs" onClick={runUserSearch}><SearchIcon style={{ fontSize: '16px' }} /></button>
            </div>
            <div className="p-2 border-bottom bg-light">
              <label className="form-label mb-1" style={{ fontSize: '12px', fontWeight: 'bold' }}>선택할 결재선 유형:</label>
              <select
                value={currentLineType}
                onChange={(e) => setCurrentLineType(e.target.value)}
                className="form-select form-select-sm border-primary"
              >
                <option value="reviewer">검토 (안전관리자)</option>
                <option value="approver">승인 (책임자)</option>
              </select>
            </div>
            <div className="org-tree-pane-content p-0">
              <table className="premium-user-table">
                <thead>
                  <tr>
                    <th width="40" className="text-center">선택</th>
                    <th>이름</th>
                    <th>직책</th>
                    <th>사번</th>
                  </tr>
                </thead>
                <tbody>
                  {displayUsers.length > 0 ? (
                    displayUsers.map(u => (
                      <UserRow
                        key={u.userid}
                        user={u}
                        isChecked={selectedRoles[currentLineType]?.userid === u.userid}
                        onAction={handleCheck}
                      />
                    ))
                  ) : (
                    <tr><td colSpan="4" className="text-center text-muted py-4">조회된 사원이 없습니다.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* 3. 우측 패널 (선택된 결재선) */}
          <div className="org-tree-pane">
            <div className="org-tree-pane-header bg-primary text-white">
              <span>✅ 구성된 결재선 (최대 3명)</span>
            </div>
            <div className="org-tree-pane-content p-3" style={{ backgroundColor: '#f8f9fa', overflowY: 'auto' }}>
              {renderSelectedCard('drafter')}
              {renderSelectedCard('reviewer')}
              {renderSelectedCard('approver')}

              <div className="alert alert-info mt-3 p-2" style={{ fontSize: '11px', lineHeight: 1.4 }}>
                <i className="bi bi-info-circle me-1"></i>
                기안(관리감독자)와 승인(책임자)는 필수 지정해야 하며, 검토(안전관리자)는 선택 사항입니다.
              </div>
            </div>
          </div>
        </div>

        <div className="org-tree-footer">
          <button className="btn btn-success px-4" onClick={handleConfirm}>확인</button>
          <button className="btn btn-secondary px-4 ms-2" onClick={onCancel}>취소</button>
        </div>
      </div>
    </div>,
    document.body
  );
}
